import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { CHAPTER_COUNT, chapterDescriptionKey, chapterPictureSlotKeys, chapterScalarKey, richTextDocumentHasContent, } from "@diality/contracts";
import { Button } from "../components/ui/button";
import { Reveal } from "../components/ui/reveal";
import { cn } from "../lib/utils";
import { BlockLink, MediaSlotView } from "../render/context";
import { resolveSlotMedia } from "../render/slot-media";
import { RichTextDocumentView } from "./rich-text";
/**
 * The three authored landing compositions. Extra CMS chapters repeat these
 * patterns in order, retaining a deliberate editorial rhythm without asking
 * authors to provide presentation coordinates.
 */
const CHAPTER_COMPOSITIONS = [
    {
        height: "@min-[901px]:h-[981px]",
        copyClassName: "@min-[901px]:left-[10.694%] @min-[901px]:top-0",
        mobileCollageHeight: "aspect-square",
        breakTitleAfterFirstWord: true,
        tiles: [
            {
                role: "detailB",
                label: "Detail image",
                depth: "small",
                className: "left-[54.861%] top-0 w-1/3 @min-[901px]:left-[68.472%] @min-[901px]:top-0 @min-[901px]:w-[20.833%]",
            },
            {
                role: "feature",
                label: "Primary image",
                depth: "large",
                className: "right-0 top-[30%] w-2/3 @min-[901px]:right-auto @min-[901px]:left-[52.708%] @min-[901px]:top-[168px] @min-[901px]:w-[34.722%]",
            },
            {
                role: "detailA",
                label: "Secondary image",
                depth: "small",
                className: "bottom-0 left-0 w-1/2 @min-[901px]:bottom-auto @min-[901px]:left-[28.264%] @min-[901px]:top-[612px] @min-[901px]:w-[20.833%]",
            },
        ],
    },
    {
        height: "@min-[901px]:h-[964px]",
        copyClassName: "@min-[901px]:left-[50.833%] @min-[901px]:top-0",
        mobileCollageHeight: "aspect-[1/1.15]",
        tiles: [
            {
                role: "detailA",
                label: "Primary image",
                depth: "small",
                className: "left-[6.806%] top-0 w-1/2 @min-[901px]:left-[10.694%] @min-[901px]:w-[20.833%]",
            },
            {
                role: "feature",
                label: "Secondary image",
                depth: "large",
                className: "left-[2%] top-[22%] w-2/3 @min-[901px]:left-[12.361%] @min-[901px]:top-[145px] @min-[901px]:w-[34.722%]",
            },
            {
                role: "detailB",
                label: "Additional image",
                depth: "small",
                className: "bottom-0 right-0 w-1/2 @min-[901px]:bottom-auto @min-[901px]:right-auto @min-[901px]:left-[50.243%] @min-[901px]:top-[595px] @min-[901px]:w-[20.833%]",
            },
        ],
    },
    {
        height: "@min-[901px]:h-[981px]",
        copyClassName: "@min-[901px]:left-[10.694%] @min-[901px]:top-0",
        mobileCollageHeight: "aspect-square",
        tiles: [
            {
                role: "detailA",
                label: "Primary image",
                depth: "small",
                className: "left-[54.861%] top-0 w-1/3 @min-[901px]:left-[68.472%] @min-[901px]:w-[20.833%]",
            },
            {
                role: "feature",
                label: "Secondary image",
                depth: "large",
                className: "right-0 top-[30%] w-2/3 @min-[901px]:right-auto @min-[901px]:left-[52.708%] @min-[901px]:top-[168px] @min-[901px]:w-[34.722%]",
                imageClassName: "object-[50%_25%]",
            },
            {
                role: "detailB",
                label: "Detail image",
                depth: "small",
                className: "bottom-0 left-0 w-1/2 @min-[901px]:bottom-auto @min-[901px]:left-[28.264%] @min-[901px]:top-[612px] @min-[901px]:w-[20.833%]",
            },
        ],
    },
];
function ChapterMedia({ chapter, composition, }) {
    return (_jsx("div", { className: cn("relative z-20 mt-[72px] w-full @min-[901px]:absolute @min-[901px]:inset-0 @min-[901px]:mt-0 @min-[901px]:aspect-auto", composition.mobileCollageHeight), children: composition.tiles.map((tile, tileIndex) => (_jsx("div", { className: cn("absolute z-10 aspect-[4/3] data-[chapters-tile-depth=large]:z-30 data-[chapters-tile-depth=medium]:z-20", tile.className), "data-chapters-tile": "", "data-chapters-tile-depth": tile.depth, children: _jsx("div", { "data-chapters-tile-drift": "", className: "size-full overflow-hidden rounded-[15px]", children: _jsx(Reveal, { asChild: true, delay: 0.08 + tileIndex * 0.06, children: _jsx("div", { "data-chapters-tile-reveal": "", className: "size-full", children: _jsx(MediaSlotView, { image: chapter[tile.role], ratio: "4 / 3", label: tile.label, className: cn("size-full rounded-none object-cover", tile.imageClassName), emptyRender: null }) }) }) }) }, tile.role))) }));
}
function Chapter({ chapter, index }) {
    const composition = CHAPTER_COMPOSITIONS[index % CHAPTER_COMPOSITIONS.length];
    const [firstTitleWord, ...remainingTitleWords] = chapter.title
        .trim()
        .split(/\s+/);
    const title = composition.breakTitleAfterFirstWord && remainingTitleWords.length > 0 ? (_jsxs(_Fragment, { children: [firstTitleWord, " ", _jsx("br", {}), remainingTitleWords.join(" ")] })) : (chapter.title);
    return (_jsx("section", { className: cn("relative h-auto min-h-0 w-full px-[6.667cqw] pb-[72px] pt-[100px] @min-[901px]:px-0 @min-[901px]:pb-0 @min-[901px]:pt-0", composition.height), "data-chapters-section": "", children: _jsxs("div", { className: "relative mx-auto h-auto w-full max-w-[1440px] @min-[901px]:h-full", children: [_jsx("div", { className: cn("relative inset-auto z-50 w-full text-center @min-[901px]:absolute @min-[901px]:w-[min(38.403%,553px)] @min-[901px]:text-left", composition.copyClassName), "data-chapters-copy": "", children: _jsxs("div", { "data-chapters-copy-drift": "", className: "flex flex-col items-center gap-[14px] @min-[901px]:items-start @min-[901px]:gap-4", children: [chapter.eyebrow && (_jsx(Reveal, { asChild: true, children: _jsx("p", { "data-chapters-eyebrow": "", "data-chapters-copy-item": "", className: "type-eyebrow-lg mb-[10px] tracking-[0.96px] text-foreground @min-[901px]:mb-2", children: chapter.eyebrow }) })), _jsx(Reveal, { asChild: true, delay: 0.08, children: _jsx("h2", { "data-chapters-copy-item": "", className: "max-w-none text-balance font-display text-[clamp(48px,14cqw,68px)] font-light leading-none tracking-[-1.2px] text-brand-blue @min-[901px]:max-w-[553px] @min-[901px]:text-[clamp(52px,7.222cqw,104px)] @min-[901px]:tracking-[-2.08px]", children: title }) }), chapter.description &&
                                richTextDocumentHasContent(chapter.description) && (_jsx(Reveal, { asChild: true, delay: 0.16, children: _jsx("div", { "data-chapters-copy-item": "", className: "max-w-none @min-[901px]:max-w-[553px]", children: _jsx(RichTextDocumentView, { document: chapter.description, className: "font-sans text-lg font-light leading-[1.55] text-foreground @min-[901px]:text-xl [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_strong]:font-bold" }) }) })), chapter.cta && (_jsx(Reveal, { asChild: true, delay: 0.24, children: _jsx("div", { "data-chapters-copy-item": "", className: "mt-3 @min-[901px]:mt-6", children: _jsx(Button, { asChild: true, children: _jsx(BlockLink, { href: chapter.cta.href, children: chapter.cta.label }) }) }) }))] }) }), _jsx(ChapterMedia, { chapter: chapter, composition: composition })] }) }));
}
/**
 * The passage itself, without block chrome — so a component harness renders
 * exactly what a page renders.
 */
export function ChaptersView({ data }) {
    if (data.chapters.length === 0)
        return null;
    return (_jsx("div", { children: data.chapters.map((chapter, index) => (_jsx(Chapter, { chapter: chapter, index: index }, index))) }));
}
export function ChaptersBlock({ data }) {
    return _jsx(ChaptersView, { data: data });
}
/**
 * Block-level, not a repeated group — and that is what makes the block
 * authorable at all: a picture held by a repeated item has no write path at any
 * layer, so a chapter list carrying its own pictures could never be filled in.
 *
 * Chapter N owns picture slots 3N-2, 3N-1 and 3N, feature first, exactly as
 * CHAPTERS_PICTURE_SLOTS declares them. The keys come from the contracts
 * helpers rather than being spelled here, so the names the inspector writes and
 * the names this reads cannot drift apart.
 */
export function chaptersFromBlock(block, resolve) {
    const scalars = block.scalars ?? {};
    const chapters = Array.from({ length: CHAPTER_COUNT }, (_, index) => {
        const position = index + 1;
        const [feature, detailA, detailB] = chapterPictureSlotKeys(position);
        const ctaLabel = scalars[chapterScalarKey(position, "CtaLabel")]?.trim();
        const ctaHref = scalars[chapterScalarKey(position, "CtaHref")]?.trim();
        return {
            eyebrow: scalars[chapterScalarKey(position, "Eyebrow")] || undefined,
            title: scalars[chapterScalarKey(position, "Title")] ?? "",
            description: block.documents?.[chapterDescriptionKey(position)],
            cta: ctaLabel && ctaHref ? { label: ctaLabel, href: ctaHref } : undefined,
            feature: resolveSlotMedia(block, feature, resolve),
            detailA: resolveSlotMedia(block, detailA, resolve),
            detailB: resolveSlotMedia(block, detailB, resolve),
        };
    });
    return { chapters };
}
export const chaptersBlockEntry = {
    Component: ChaptersBlock,
    fromBlock: chaptersFromBlock,
};
//# sourceMappingURL=chapters.js.map